import { useEffect, useState } from 'react';
import { Box, Pagination, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from '@src/styles';
import { cardTitleSX, footnoteSX, monoFontFamily, paginationSX } from '../utils/format';

export interface LeaderRow {
  key: string;
  label: string;
  total: number;
  failed: number;
}

const PAGE_SIZE = 8;

const getRateColor = (rate: number | null): string => {
  if (rate === null || rate >= 99) return colors.brand.textSecondary;
  return rate >= 95 ? colors.status.pending : colors.status.rejected;
};

export function Leaderboard({
  title,
  rows,
  onSelect,
}: {
  title: string;
  rows: LeaderRow[];
  onSelect: (key: string) => void;
}) {
  const max = Math.max(...rows.map((r) => r.total), 1);
  const totalPages = Math.max(Math.ceil(rows.length / PAGE_SIZE), 1);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography component="h2" sx={{ ...cardTitleSX, mb: 1.25 }}>
        {title}
      </Typography>
      {rows.length === 0 && (
        <Box sx={{ flex: 1, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13px' }}>Nada no período.</Typography>
        </Box>
      )}
      <Box sx={{ marginX: -1 }}>
        {pageRows.map((r, index) => {
          const ok = r.total - r.failed;
          const rateValue = r.total ? (ok / r.total) * 100 : null;
          const rate = rateValue === null ? '—' : `${rateValue.toFixed(1)}%`;
          return (
            <Box
              key={r.key}
              component="button"
              type="button"
              onClick={() => onSelect(r.key)}
              title={r.label}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '22px minmax(0, 1fr) minmax(60px, 110px) 52px 50px 16px',
                alignItems: 'center',
                gap: 1.25,
                paddingX: 1,
                paddingY: 0.875,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '& .leader-chevron': { opacity: 0, transition: 'opacity 0.15s ease' },
                '&:hover, &:focus-visible': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                '&:hover .leader-chevron, &:focus-visible .leader-chevron': { opacity: 1 },
                '&:focus-visible': { outline: `1px solid ${colors.primary.yellow}` },
              }}
            >
              <Typography sx={{ fontFamily: monoFontFamily, fontSize: '11px', color: colors.brand.textMuted }}>
                {String((page - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: colors.primary.white,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.label}
              </Typography>
              <Box
                sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.06)', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(r.total / max) * 100}%`,
                    background: `linear-gradient(90deg, ${colors.chart.primary}, ${colors.primary.yellow})`,
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: monoFontFamily,
                  fontSize: '12px',
                  textAlign: 'right',
                  color: colors.brand.textStrong,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {r.total.toLocaleString('pt-BR')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: monoFontFamily,
                  fontSize: '11px',
                  textAlign: 'right',
                  color: getRateColor(rateValue),
                }}
              >
                {rate}
              </Typography>
              <ChevronRightIcon className="leader-chevron" sx={{ fontSize: 16, color: colors.brand.textMuted }} />
            </Box>
          );
        })}
      </Box>
      {rows.length > PAGE_SIZE && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            pt: 1.25,
            mt: 'auto',
            borderTop: `1px solid ${colors.brand.line}`,
          }}
        >
          <Typography sx={footnoteSX}>
            {rows.length} itens · página {page}/{totalPages}
          </Typography>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" sx={paginationSX} />
        </Box>
      )}
    </Box>
  );
}
